import { useState } from 'react';
import { Scale } from 'lucide-react';
import { VoteCard, type DAOVote } from './VoteCard';

// ---------------------------------------------------------------------------
// Sample data — 4 votes covering all vote types
// ---------------------------------------------------------------------------

const SAMPLE_VOTES: DAOVote[] = [
  {
    id: 'vote-1',
    vehicleId: '1',
    vehicleDisplay: '2022 Toyota Camry',
    voteType: 'maintenance',
    title: 'Replace Front Brake Pads & Rotors',
    description:
      'The vehicle is due for front brake maintenance at 45,000 miles. An inspection by our preferred shop (AutoCare Pro) estimates $380 for pads and rotors. This will be deducted from the next payment cycle reserve fund. The shop has confirmed availability this week. Delaying may lead to rotor damage and significantly higher costs.',
    proposedBy: 'Marcus T.',
    daysRemaining: 5,
    votesFor: 4,
    votesAgainst: 1,
    totalVoters: 7,
    userVote: null,
  },
  {
    id: 'vote-2',
    vehicleId: '2',
    vehicleDisplay: '2021 Honda Accord',
    voteType: 'disposal',
    title: 'Authorize Vehicle Sale — End of Term',
    description:
      'The retail contract on this vehicle has reached its 24-month term. The creator has fulfilled payment obligations. A dealer buyout offer of $19,800 is on the table, which represents an estimated 8% return on original LLC equity. Voting to authorize disposal will trigger the off-platform LLC dissolution process and distribute proceeds to investors proportionally.',
    proposedBy: 'Elena S.',
    daysRemaining: 2,
    votesFor: 6,
    votesAgainst: 2,
    totalVoters: 10,
    userVote: 'for',
  },
  {
    id: 'vote-3',
    vehicleId: '1',
    vehicleDisplay: '2022 Toyota Camry',
    voteType: 'grace_disclosure',
    title: 'Disclose Gift Contributions to Forgive Arrears',
    description:
      'The creator has missed two payments and is currently $840 in arrears. Three supporters have indicated willingness to disclose their contributions as gifts, which would forgive the outstanding balance and keep the contract current. A majority vote is required before any disclosure is made. Once approved, the disclosures are irrevocable.',
    proposedBy: 'Danielle R.',
    daysRemaining: 1,
    votesFor: 3,
    votesAgainst: 0,
    totalVoters: 7,
    userVote: null,
  },
  {
    id: 'vote-4',
    vehicleId: '3',
    vehicleDisplay: '2023 Ford F-150',
    voteType: 'refinance',
    title: 'Refinance Retail Contract at Lower Rate',
    description:
      'A partnering credit union has offered to refinance the retail contract from 18.9% APR to 13.4% APR, reducing the monthly payment by approximately $112. This would extend the contract term by 6 months but improve cash flow and lower default risk. The LLC would retain the same ownership structure with updated financing terms.',
    proposedBy: 'Bianca M.',
    daysRemaining: 7,
    votesFor: 2,
    votesAgainst: 1,
    totalVoters: 5,
    userVote: null,
  },
];

// ---------------------------------------------------------------------------
// DAOGovernancePanel
// ---------------------------------------------------------------------------

interface DAOGovernancePanelProps {
  vehicleFilter?: string;
}

export function DAOGovernancePanel({ vehicleFilter }: DAOGovernancePanelProps) {
  const [votes, setVotes] = useState<DAOVote[]>(SAMPLE_VOTES);

  const filteredVotes = vehicleFilter
    ? votes.filter((v) => v.vehicleId === vehicleFilter)
    : votes;

  const needsVoteCount = filteredVotes.filter((v) => v.userVote === null).length;

  function handleVote(id: string, choice: 'for' | 'against') {
    setVotes((prev) =>
      prev.map((v) => {
        if (v.id !== id) return v;
        // Optimistically update tally
        const wasFor = v.userVote === 'for';
        const wasAgainst = v.userVote === 'against';
        return {
          ...v,
          userVote: choice,
          votesFor:
            choice === 'for'
              ? v.votesFor + (wasFor ? 0 : 1)
              : v.votesFor - (wasFor ? 1 : 0),
          votesAgainst:
            choice === 'against'
              ? v.votesAgainst + (wasAgainst ? 0 : 1)
              : v.votesAgainst - (wasAgainst ? 1 : 0),
        };
      })
    );
  }

  return (
    <section>
      {/* Header */}
      <div className="flex items-start gap-3 mb-2">
        <Scale className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
        <div>
          <h2 className="text-xl font-bold text-gray-900 leading-tight">Governance</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Your vote matters as a co-owner. All decisions are binding.
          </p>
        </div>
      </div>

      {/* Vote count summary */}
      {filteredVotes.length > 0 && (
        <p className="text-sm text-gray-500 mb-6 ml-8">
          <span className="font-semibold text-gray-700">{filteredVotes.length}</span> active vote
          {filteredVotes.length !== 1 ? 's' : ''}
          {needsVoteCount > 0 && (
            <>
              {' · '}
              <span className="font-semibold text-amber-600">{needsVoteCount}</span> need
              {needsVoteCount === 1 ? 's' : ''} your vote
            </>
          )}
        </p>
      )}

      {/* Grid */}
      {filteredVotes.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {filteredVotes.map((vote) => (
            <VoteCard key={vote.id} vote={vote} onVote={handleVote} />
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-400 text-sm">No active votes</p>
        </div>
      )}
    </section>
  );
}
